/**
 * Nhiệm vụ hằng ngày + đơn lớn có thời hạn (M3).
 *
 * - Mỗi ngày máy (YYYY-MM-DD theo giờ máy, reset 0 giờ) tạo 5 nhiệm vụ từ 8 mẫu,
 *   xoay vòng deterministic theo ngày — nguồn XP chính cho nhịp 250 XP/giờ thực
 *   cùng đơn lớn (master-plan 2.4).
 * - Tiến độ cập nhật mỗi tick từ số liệu bán hàng thật (không còn tích lũy đều
 *   như M1). Hoàn thành: +xu, +XP ngay.
 * - Đơn lớn: xuất hiện mỗi M09 giờ game, có thời hạn (giờ game), cần nhận rồi
 *   giao đủ hàng từ kho; thưởng xu + xu cao cấp + XP. Hết hạn thì mất.
 */
import { congXp } from './xp.js';
import { layThamSoMoPhong } from './du-lieu.js';
import type { HamRng } from './rng.js';
import type { DonLonMau, DuLieuGame, NhiemVuHangNgay, TrangThaiGame } from './types.js';

/** Hash chuỗi đơn giản, deterministic (xoay nhiệm vụ theo ngày). */
export function bamChuoi(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Ngày máy hiện tại dạng YYYY-MM-DD (giờ máy, không phải ngày game). */
export function ngayMayHienTai(ngay = new Date()): string {
  const y = ngay.getFullYear();
  const m = String(ngay.getMonth() + 1).padStart(2, '0');
  const d = String(ngay.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Ngày máy (giờ máy) ứng với giờ game thứ N kể từ mốc (1 giờ game = 1 phút thực). */
export function ngayMayTuGioGame(moc: Date, gioGameDaQua: number): string {
  return ngayMayHienTai(new Date(moc.getTime() + gioGameDaQua * 60_000));
}

/** Các mẫu NV đủ điều kiện mở (theo cấp người chơi). */
export function mauNhiemVuMo(state: TrangThaiGame, duLieu: DuLieuGame): NhiemVuHangNgay[] {
  return duLieu.nhiemVu.nhiemVuHangNgay.filter((m) => state.nguoiChoi.cap >= m.capNguoiChoiMo);
}

/**
 * Tạo 5 nhiệm vụ cho một ngày máy: lọc theo cấp, sắp theo mã, xoay vòng theo
 * hash ngày rồi lấy 5. Deterministic — cùng ngày luôn ra cùng bộ.
 */
export function taoNhiemVuNgay(state: TrangThaiGame, duLieu: DuLieuGame, ngayMay: string): void {
  const mo = [...mauNhiemVuMo(state, duLieu)].sort((a, b) => (a.ma < b.ma ? -1 : 1));
  const chon: NhiemVuHangNgay[] = [];
  if (mo.length > 0) {
    const batDau = bamChuoi(ngayMay) % mo.length;
    const soLuong = Math.min(layThamSoMoPhong(duLieu, 'M07'), mo.length);
    for (let i = 0; i < soLuong; i++) {
      chon.push(mo[(batDau + i) % mo.length]!);
    }
  }
  state.nhiemVuNgay = ngayMay;
  state.nhiemVu = chon.map((m) => ({ ma: m.ma, tienDo: 0, hoanThanh: false }));
}

/** Kiểm tra sang ngày máy mới thì tạo lại nhiệm vụ. */
export function kiemTraSangNgayMoi(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  ngayMay: string,
): boolean {
  if (state.nhiemVuNgay !== ngayMay) {
    taoNhiemVuNgay(state, duLieu, ngayMay);
    return true;
  }
  return false;
}

export interface ThongTinBanHangTick {
  /** Mã nhóm hàng -> số món bán trong tick. */
  monTheoNhom: Record<string, number>;
  /** Tên kiểu khách -> số lượt trong tick. */
  luotTheoKieu: Record<string, number>;
  /** Doanh thu tick (lãi gộp + vốn hàng bán). */
  doanhThu: number;
  /** Số cốc cà phê bán trong tick. */
  cocCaPhe: number;
}

function tienDoTangThem(mau: NhiemVuHangNgay, thongTin: ThongTinBanHangTick): number {
  switch (mau.loaiMucTieu) {
    case 'banMonNhom':
      return thongTin.monTheoNhom[mau.maMucTieu] ?? 0;
    case 'phucVuKieuKhach':
      return thongTin.luotTheoKieu[mau.maMucTieu] ?? 0;
    case 'doanhThu':
      return thongTin.doanhThu;
    case 'banCaPhe':
      return thongTin.cocCaPhe;
    default:
      return 0;
  }
}

/**
 * Cập nhật tiến độ NV từ số liệu tick. Trả về mã các NV vừa hoàn thành
 * (UI dùng để hiện toast + phát SFX).
 */
export function capNhatTienDoNhiemVu(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  thongTin: ThongTinBanHangTick,
): string[] {
  const vuaXong: string[] = [];
  const mauTheoMa = new Map(duLieu.nhiemVu.nhiemVuHangNgay.map((m) => [m.ma, m]));
  for (const nv of state.nhiemVu) {
    if (nv.hoanThanh) continue;
    const mau = mauTheoMa.get(nv.ma);
    if (!mau) continue;
    nv.tienDo += tienDoTangThem(mau, thongTin);
    if (nv.tienDo >= mau.soLuong) {
      nv.hoanThanh = true;
      state.xu += mau.thuongXu;
      congXp(state, duLieu, mau.thuongXp);
      state.thongKe.soNvHoanThanh += 1;
      vuaXong.push(nv.ma);
    }
  }
  return vuaXong;
}

// ---------------------------------------------------------------------------
// Đơn lớn có thời hạn
// ---------------------------------------------------------------------------

/** Chuyển 'SP01:10;SP07:8' thành Record. Ném lỗi nếu định dạng sai. */
export function phanTichYeuCau(yeuCau: string): Record<string, number> {
  const ketQua: Record<string, number> = {};
  for (const phan of yeuCau.split(';')) {
    const p = phan.trim();
    if (!p) continue;
    const [ma, sl] = p.split(':');
    const soLuong = Number(sl);
    if (!ma || !Number.isInteger(soLuong) || soLuong <= 0) {
      throw new Error(`Yêu cầu đơn lớn sai định dạng: "${yeuCau}".`);
    }
    ketQua[ma.trim()] = soLuong;
  }
  if (Object.keys(ketQua).length === 0) {
    throw new Error(`Yêu cầu đơn lớn rỗng: "${yeuCau}".`);
  }
  return ketQua;
}

/** Các mẫu đơn lớn mà NPC giao đã mở (theo cấp người chơi). */
export function mauDonLonMo(state: TrangThaiGame, duLieu: DuLieuGame): DonLonMau[] {
  const npcTheoMa = new Map(duLieu.npc.npc.map((n) => [n.ma, n]));
  return duLieu.nhiemVu.donLonMau.filter((m) => {
    if (state.nguoiChoi.cap < 2) return false;
    const npc = npcTheoMa.get(m.npcGiao);
    if (!npc) return false;
    return state.nguoiChoi.cap >= npc.capNguoiChoiMo;
  });
}

/**
 * Xử lý đơn lớn mỗi giờ game: đếm ngược tạo mới, hoặc đếm ngược hết hạn.
 * Gọi 1 lần mỗi tick.
 */
export function xuLyDonLonTheoGio(state: TrangThaiGame, duLieu: DuLieuGame, _rng: HamRng): void {
  const chuKy = layThamSoMoPhong(duLieu, 'M09');
  if (!state.donLon) {
    state.donLonDemNguoc -= 1;
    if (state.donLonDemNguoc <= 0) {
      const mo = mauDonLonMo(state, duLieu);
      if (mo.length > 0) {
        const mau = mo[state.thongKe.soDonLon % mo.length]!;
        state.donLon = {
          ma: mau.ma,
          yeuCau: phanTichYeuCau(mau.yeuCau),
          hanConLai: mau.hanGioGame,
          daChapNhan: false,
        };
        state.thongKe.soDonLon += 1;
      }
      state.donLonDemNguoc = chuKy;
    }
    return;
  }
  state.donLon.hanConLai -= 1;
  if (state.donLon.hanConLai <= 0) {
    // Hết hạn: đơn biến mất, chờ chu kỳ sau.
    state.donLon = null;
    state.donLonDemNguoc = chuKy;
  }
}

/** Nhận đơn lớn (miễn phí). Ném lỗi nếu không có đơn hoặc đã nhận. */
export function chapNhanDonLon(state: TrangThaiGame): void {
  if (!state.donLon) throw new Error('Hiện không có đơn lớn nào.');
  if (state.donLon.daChapNhan) throw new Error('Đơn này đã được nhận rồi.');
  state.donLon.daChapNhan = true;
}

/** Kiểm tra kho có đủ hàng giao đơn lớn không; trả về danh sách thiếu. */
export function hangThieuDonLon(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
): Array<{ ma: string; ten: string; can: number; co: number }> {
  void duLieu;
  if (!state.donLon) return [];
  const tenTheoMa = new Map<string, string>();
  for (const m of duLieu.matHang) tenTheoMa.set(m.ma, m.ten);
  const thieu: Array<{ ma: string; ten: string; can: number; co: number }> = [];
  for (const [ma, can] of Object.entries(state.donLon.yeuCau)) {
    const co = state.kho[ma]?.ton ?? 0;
    if (co < can) thieu.push({ ma, ten: tenTheoMa.get(ma) ?? ma, can, co });
  }
  return thieu;
}

export interface KetQuaGiaoDonLon {
  xu: number;
  xuCaoCap: number;
  xp: number;
}

/**
 * Giao đơn lớn: trừ kho, cộng thưởng. Ném lỗi nếu chưa nhận hoặc thiếu hàng.
 */
export function giaoDonLon(state: TrangThaiGame, duLieu: DuLieuGame): KetQuaGiaoDonLon {
  if (!state.donLon) throw new Error('Hiện không có đơn lớn nào.');
  if (!state.donLon.daChapNhan) throw new Error('Hãy nhận đơn trước khi giao.');
  const thieu = hangThieuDonLon(state, duLieu);
  if (thieu.length > 0) {
    const moTa = thieu.map((t) => `${t.ten} (cần ${t.can}, có ${t.co})`).join(', ');
    throw new Error(`Thiếu hàng để giao đơn: ${moTa}.`);
  }
  const mau = duLieu.nhiemVu.donLonMau.find((m) => m.ma === state.donLon!.ma);
  if (!mau) throw new Error(`Mẫu đơn lớn lạ: ${state.donLon.ma}.`);
  for (const [ma, can] of Object.entries(state.donLon.yeuCau)) {
    state.kho[ma]!.ton -= can;
  }
  state.xu += mau.thuongXu;
  state.xuCaoCap += mau.thuongXuCaoCap;
  congXp(state, duLieu, mau.thuongXp);
  state.thongKe.soDonLonHoanThanh += 1;
  state.donLon = null;
  state.donLonDemNguoc = layThamSoMoPhong(duLieu, 'M09');
  return { xu: mau.thuongXu, xuCaoCap: mau.thuongXuCaoCap, xp: mau.thuongXp };
}
